import { createFileRoute } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import {
  Header,
  HeaderBackButton,
  HeaderCopyButton,
  HeaderSidebarTrigger,
  HeaderUserMenu,
} from '@/app/ui/components/header';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  AlertSquareIcon,
  Copy01Icon,
  DocumentValidationIcon,
  SparklesIcon,
  Tick02Icon,
} from '@hugeicons/core-free-icons';
import {
  MainContainer,
  MainHeader,
  MainHeaderBackButton,
  MainScrollArea,
} from '@/app/ui/components/main';
import { H1, H2 } from '@/app/ui/components/heading';
import { useContext } from 'react';
import {
  LearningsFramePanel,
  LearningsFramePanelBadge,
  LearningsFramePanelCount,
  LearningsFramePanelHelpIcon,
  LearningsFramePanelText,
} from '@/app/components/features/learnings/LearningsFramePanel';
import { AppContext } from '@/app/appContext';
import { ProjectTypeLabelsEnum } from '@/app/utils/constants';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { useGetLearningsQuery } from '@/app/hooks/queries/useLearnings';
import { Frame } from '@/app/ui/components/frame';
import { Icons } from '@/app/ui/components/icons';
import {
  Alert,
  AlertAction,
  AlertActionToolbar,
  AlertActionToolbarButton,
  AlertDescription,
  AlertDescriptionCollapsibleText,
  AlertTitle,
} from '@/app/ui/components/alert';
import { Spinner } from '@/app/ui/components/spinner';
import { Button } from '@/app/ui/components/button';
import { LearningsStatements } from '@/app/components/features/learnings/LearningsStatements';
import { useCopyToClipboard } from '@/app/ui/hooks/use-copy-to-clipboard';
import { format } from 'date-fns';
import { dateFormats } from '@/app/utils/constants';
import { Link } from '@tanstack/react-router';

export const Route = createFileRoute(
  '/_authed/(organization)/_layout/learnings/portfolio/$projectType',
)({
  component: RouteComponent,
  params: {
    parse: (params) => ({ projectType: params.projectType as ProjectTypeLabelsEnum }),
  },
});

function RouteComponent() {
  const { projectType } = Route.useParams();
  const t = useTranslations('pages.learnings');
  const tGeneral = useTranslations('general');
  const { userMeta } = useContext(AppContext);
  const organizationId = userMeta?.organizationId ?? '';
  const { data, isLoading, error } = useGetLearningsQuery({
    organizationId,
    enabled: !!organizationId,
    projectType,
  });

  const copy = useCopyToClipboard();

  const learning = data?.insights?.[0];
  if (isLoading) return <Spinner kind="inset" />;
  if (!learning) return null;

  return (
    <>
      <Header>
        <HeaderBackButton />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink to={'/learnings'}>
                <HugeiconsIcon icon={SparklesIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>
                <ProjectTypeIcon type={projectType} />
                <span className="line-clamp-1">
                  {tGeneral(`projectType.${projectType.toLowerCase() as ProjectTypeLabelsEnum}`)}{' '}
                  <span className="lowercase">{t('title')}</span>
                </span>
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderUserMenu />
        <HeaderCopyButton />
      </Header>
      <MainScrollArea>
        <MainHeader>
          <H1>
            <MainHeaderBackButton />
            {tGeneral(`projectType.${projectType.toLowerCase() as ProjectTypeLabelsEnum}`)}{' '}
            <span className="lowercase">{t('title')}</span>
          </H1>
        </MainHeader>
        <MainContainer className="pb-0">
          <ActivityOverview />
          <section>
            <H2 className="mb-3 mt-5">
              {t('summary')}{' '}
              {learning.lastUpdatedAt ? (
                <span className="text-muted font-normal ml-1 text-sm">
                  {t('lastUpdated', {
                    date: format(learning.lastUpdatedAt, dateFormats.dayFirst),
                  })}
                </span>
              ) : null}
            </H2>
            <Alert>
              <AlertTitle className="flex items-start">
                <HugeiconsIcon
                  icon={SparklesIcon}
                  className="text-primary-11 inline-block size-4.5 mr-1.5 mb-1"
                />
                {tGeneral('aiSummary')}
                <AlertActionToolbar>
                  <AlertActionToolbarButton
                    label={copy.copied ? tGeneral('copied') : tGeneral('copyToClipboard')}
                    onClick={() => copy.trigger(learning.summary)}
                  >
                    <HugeiconsIcon icon={copy.copied ? Tick02Icon : Copy01Icon} />
                  </AlertActionToolbarButton>
                </AlertActionToolbar>
              </AlertTitle>
              <AlertDescription>
                <AlertDescriptionCollapsibleText maxLength={600} text={learning.summary} />
              </AlertDescription>
            </Alert>
          </section>
        </MainContainer>
        <LearningsStatements data={data} />
      </MainScrollArea>
    </>
  );
}

function ActivityOverview() {
  const { projectType } = Route.useParams();
  const t = useTranslations('pages.learnings');
  const { userMeta } = useContext(AppContext);
  const organizationId = userMeta?.organizationId ?? '';
  const { data, isLoading, error } = useGetLearningsQuery({
    organizationId,
    enabled: !!organizationId,
    projectType,
  });

  const total = data?.stats.totalUploaded ?? 0;

  return (
    <section>
      <H2 className="mb-3">{t('section1.title')}</H2>
      <Frame stackedPanels className="@2xl:grid-cols-3">
        <LearningsFramePanel>
          <LearningsFramePanelText>
            <LearningsFramePanelCount count={total} isLoading={isLoading} />
            {t('section1.1')}
            <LearningsFramePanelHelpIcon>{t('section1.1Tooltip')}</LearningsFramePanelHelpIcon>
          </LearningsFramePanelText>
          <LearningsFramePanelBadge className="text-primary-11">
            <Icons.evaluations />
          </LearningsFramePanelBadge>
        </LearningsFramePanel>
        <LearningsFramePanel>
          <LearningsFramePanelText>
            <LearningsFramePanelCount count={data?.stats.won ?? 0} isLoading={isLoading} />
            {t('section1.2')}
            <LearningsFramePanelHelpIcon>{t('section1.2Tooltip')}</LearningsFramePanelHelpIcon>
          </LearningsFramePanelText>
          <LearningsFramePanelBadge className="text-success">
            <HugeiconsIcon icon={DocumentValidationIcon} />
          </LearningsFramePanelBadge>
        </LearningsFramePanel>
        <LearningsFramePanel>
          <LearningsFramePanelText>
            <LearningsFramePanelCount count={data?.stats.lost ?? 0} isLoading={isLoading} />
            {t('section1.3')}
            <LearningsFramePanelHelpIcon>{t('section1.3Tooltip')}</LearningsFramePanelHelpIcon>
          </LearningsFramePanelText>
          <LearningsFramePanelBadge className="text-destructive">
            <Icons.documentXIcon />
          </LearningsFramePanelBadge>
        </LearningsFramePanel>
      </Frame>
      {!isLoading && total < 10 ? (
        <Alert variant="warning" className="mt-3">
          <HugeiconsIcon icon={AlertSquareIcon} />
          <AlertTitle>{t('warningTitle')}</AlertTitle>
          <AlertDescription>{t('warningDescription')}</AlertDescription>
          <AlertAction>
            <Button
              nativeButton={false}
              variant="secondary"
              render={<Link to="/knowledge-base/new-feedback">{t('warningAction')}</Link>}
            />
          </AlertAction>
        </Alert>
      ) : null}
    </section>
  );
}
